import {Component, computed, inject, AfterViewInit, OnDestroy, ChangeDetectionStrategy} from "@angular/core"
import {toSignal, takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {RouterModule, NavigationEnd, NavigationSkipped} from "@angular/router"
import {SortCriterion} from "@common/models/item/list-item"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {ContentTypeModel, MutationCreateOrganizationInput, MutationUpdateOrganizationInput} from "@generated"
import {OrganizationListItemFragment} from "@platform/pages/organizations/organizations-page.generated"
import {CardErrorComponent, CardPlaceholderComponent} from "@common/components/cards"
import {CheckboxesFilterComponent, SearchFilterComponent} from "@common/components/filters"
import {ItemFiltersComponent, ItemListComponent, ListInfoComponent} from "@common/components/item"
import {InfiniteListComponent} from "@common/components/lists"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {GetOrganizationListItemsGQL} from "@pages/organizations/organizations-page.generated"
import {SidebarLayoutComponent} from "@platform/components/layouts/sidebar-layout/sidebar-layout.component"
import {EntityCardComponent} from "@common/components/entity/entity-card/entity-card.component"
import {AddOrganizationDialogComponent} from "@platform/components/organizations/add-organization-dialog/add-organization-dialog.component"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {filter as rxFilter} from "rxjs/operators"

// Highlight the badge green while the "last active" label still reads "1 day ago" or fresher.
// date-fns' formatDistanceToNow switches from "1 day" to "2 days" at 42 hours (its
// MINUTES_IN_ALMOST_TWO_DAYS = 2520 boundary), so match that here to keep colour and label in sync.
const recentActivityWindowMs = 42 * 60 * 60 * 1000

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CardErrorComponent,
        ItemFiltersComponent,
        SearchFilterComponent,
        InfiniteListComponent,
        ListInfoComponent,
        CardPlaceholderComponent,
        EntityCardComponent,
        RouterModule,
        SidebarLayoutComponent,
        CheckboxesFilterComponent,
        TimeAgoPipe,
    ],
    selector: "cm-organizations-page",
    styleUrls: ["./organizations-page.component.scss"],
    templateUrl: "./organizations-page.component.html",
})
export class OrganizationsPageComponent
    extends ItemListComponent<OrganizationListItemFragment, MutationUpdateOrganizationInput, Partial<MutationCreateOrganizationInput>>
    implements AfterViewInit, OnDestroy
{
    private readonly dialogService = inject(MatDialog)
    private currentDialogRef: MatDialogRef<AddOrganizationDialogComponent> | null = null

    // OVERLOADS
    protected override _contentTypeModel = ContentTypeModel.Organization
    readonly itemsGql = inject(GetOrganizationListItemsGQL)

    protected _fetchList = ({skip, take}: {skip: number; take: number}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: take,
            skip: skip,
            filter: this.filters.organizationFilter(),
            orderBy: this.filters.organizationOrderBy(),
        }).then(({organizations, organizationsCount}) => ({items: organizations, totalCount: organizationsCount}))

    protected _refreshItem = ({id, legacyId}: {id?: string; legacyId?: number}) =>
        fetchThrowingErrors(this.itemsGql)({
            take: 1,
            filter: {...this.filters.organizationFilter(), id: id ? {equals: id} : undefined, legacyId: legacyId ? {equals: legacyId} : undefined},
        }).then(({organizations}) => organizations?.[0] || undefined)

    isRecentlyActive(lastActiveAt: string | null | undefined): boolean {
        return !!lastActiveAt && Date.now() - new Date(lastActiveAt).getTime() < recentActivityWindowMs
    }

    private readonly $currentSort = toSignal(
        this.filters.makeObservable$((states) => (states.criteria.sort ? Array.from(states.criteria.sort)[0] : undefined)),
        {initialValue: undefined},
    )

    protected readonly $sortCriteria = computed<SortCriterion[]>(() => {
        // No explicit choice falls back to the backend default order (name ascending),
        // so show that as selected until the user picks something else.
        const current = this.$currentSort() ?? "name_asc"
        const make = (label: string, code: string): SortCriterion => ({
            label,
            active: current === code,
            operation: () => this.filters.updateCriteria("sort", [code]),
        })
        return [
            make("Name (A–Z)", "name_asc"),
            make("Name (Z–A)", "name_desc"),
            make("Newest first", "created_desc"),
            make("Oldest first", "created_asc"),
            make("Recently active", "active_desc"),
            make("Least recently active", "active_asc"),
        ]
    })

    override ngAfterViewInit() {
        super.ngAfterViewInit()
        // Check initial route state
        this.checkAndHandleRoute()

        // Listen for navigation events to handle create dialog opening/closing
        // We need to handle both NavigationEnd and NavigationSkipped because when
        // two navigations happen simultaneously, the first gets cancelled and
        // the second gets skipped, but the URL still changes.
        this.router.events
            .pipe(
                rxFilter((event): event is NavigationEnd | NavigationSkipped => event instanceof NavigationEnd || event instanceof NavigationSkipped),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.checkAndHandleRoute()
            })
    }

    ngOnDestroy() {
        if (this.currentDialogRef) {
            this.currentDialogRef.close()
        }
    }

    private checkAndHandleRoute() {
        const currentUrl = this.router.url
        const isCreateRoute = currentUrl.includes("/create")

        if (isCreateRoute && !this.currentDialogRef) {
            // Open the new dialog component
            this.currentDialogRef = this.dialogService.open(AddOrganizationDialogComponent, {
                data: {},
            })

            this.currentDialogRef.afterClosed().subscribe((result: {id: string; legacyId: number} | undefined) => {
                this.currentDialogRef = null
                if (result?.id) {
                    // Navigate to the created organization
                    void this.router.navigate([result.id], {
                        relativeTo: this.route,
                        queryParamsHandling: "preserve",
                    })
                } else {
                    // Navigate back to list
                    void this.router.navigate(["../"], {
                        relativeTo: this.route,
                        queryParamsHandling: "preserve",
                        replaceUrl: true,
                    })
                }
            })
        } else if (!isCreateRoute && this.currentDialogRef) {
            this.currentDialogRef.close()
            this.currentDialogRef = null
        }
    }
}
